import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { MagnifyingGlass, Star, ClockCounterClockwise } from "@phosphor-icons/react";
import { API } from "@/lib/api";

const SuggestItem = ({ s, active, onPick, idx }) => (
  <li>
    <button
      type="button"
      data-testid={`search-suggestion-${idx}`}
      onMouseDown={(e) => { e.preventDefault(); onPick(s); }}
      className={`flex w-full items-center gap-3 px-3 py-2 text-left transition-colors duration-150 ${active ? "bg-amber/15" : "hover:bg-white/[0.06]"}`}
    >
      {s.thumb ? <img src={s.thumb} alt="" className="h-12 w-9 shrink-0 object-cover" /> : <div className="h-12 w-9 shrink-0 bg-white/5" />}
      <span className="min-w-0 flex-1">
        <span className="block truncate font-serif text-sm text-[#EDEDED]">{s.title}</span>
        <span className="block truncate font-mono text-[10px] uppercase tracking-[0.12em] text-white/40">
          {[s.release_year, s.directors?.[0] && `Dir. ${s.directors[0]}`].filter(Boolean).join(" · ")}
        </span>
      </span>
      {s.indie_pick && <Star size={12} weight="fill" className="shrink-0 text-amber" aria-label="Indie Pick" />}
    </button>
  </li>
);

// Search input with a live typeahead dropdown; picking a title jumps straight to its watch page.
const RECENT_KEY = "rv_recent_searches";
const loadRecent = () => {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); } catch { return []; }
};
const saveRecent = (q) => {
  const t = q.trim();
  if (t.length < 2) return;
  const next = [t, ...loadRecent().filter((x) => x.toLowerCase() !== t.toLowerCase())].slice(0, 5);
  localStorage.setItem(RECENT_KEY, JSON.stringify(next));
};

const RecentList = ({ items, onPick, onClear, testid }) => (
  <div data-testid={`${testid}-recent`} className="py-1">
    <div className="flex items-center justify-between px-3 py-1.5">
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/35">Recent searches</span>
      <button type="button" data-testid={`${testid}-recent-clear`} onMouseDown={(e) => { e.preventDefault(); onClear(); }} className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/40 hover:text-amber">
        Clear
      </button>
    </div>
    {items.map((q, i) => (
      <button
        key={q}
        type="button"
        data-testid={`${testid}-recent-${i}`}
        onMouseDown={(e) => { e.preventDefault(); onPick(q); }}
        className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm text-white/75 transition-colors duration-150 hover:bg-white/[0.06] hover:text-white"
      >
        <ClockCounterClockwise size={15} className="shrink-0 text-white/35" /> <span className="truncate">{q}</span>
      </button>
    ))}
  </div>
);

export const SearchSuggest = ({ value, setValue, testid, inputClass, iconClass }) => {
  const [items, setItems] = useState([]);
  const [recent, setRecent] = useState(loadRecent);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const navigate = useNavigate();
  const req = useRef(0);

  useEffect(() => {
    const q = value.trim();
    if (q.length < 2) { setItems([]); return undefined; }
    const id = ++req.current;
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/search/suggest`, { params: { q } });
        if (id === req.current) { setItems(data.items); setActive(-1); }
      } catch { /* typeahead is best-effort */ }
    }, 180);
    return () => clearTimeout(t);
  }, [value]);

  const pick = (s) => { saveRecent(value); setOpen(false); navigate(`/watch/${s.video_id}`); };
  const onKey = (e) => {
    if (e.key === "Enter" && !(open && active >= 0 && items.length)) { saveRecent(value); setRecent(loadRecent()); return; }
    if (!open || !items.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => (a + 1) % items.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => (a <= 0 ? items.length - 1 : a - 1)); }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); pick(items[active]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <>
      <MagnifyingGlass size={18} className={iconClass} />
      <input
        data-testid={testid}
        type="text"
        value={value}
        onChange={(e) => { setValue(e.target.value); setOpen(true); }}
        onFocus={() => { setRecent(loadRecent()); setOpen(true); }}
        onBlur={() => { saveRecent(value); setOpen(false); }}
        onKeyDown={onKey}
        placeholder="Search films, directors, cast…"
        autoComplete="off"
        className={inputClass}
      />
      {open && value.trim().length < 2 && recent.length > 0 && (
        <div className="absolute left-0 top-full z-[60] mt-1 w-full min-w-[320px] border border-white/10 bg-[#0B0B0B]/95 shadow-[0_18px_50px_rgba(0,0,0,0.7)] backdrop-blur-xl">
          <RecentList
            items={recent}
            testid={testid}
            onPick={(q) => { setValue(q); saveRecent(q); setRecent(loadRecent()); }}
            onClear={() => { localStorage.removeItem(RECENT_KEY); setRecent([]); }}
          />
        </div>
      )}
      {open && value.trim().length >= 2 && items.length > 0 && (
        <ul
          data-testid={`${testid}-suggestions`}
          className="absolute left-0 top-full z-[60] mt-1 max-h-[70vh] w-full min-w-[320px] overflow-y-auto border border-white/10 bg-[#0B0B0B]/95 py-1 shadow-[0_18px_50px_rgba(0,0,0,0.7)] backdrop-blur-xl"
        >
          {items.map((s, i) => <SuggestItem key={s.video_id} s={s} idx={i} active={i === active} onPick={pick} />)}
        </ul>
      )}
    </>
  );
};
